<template>
  <iColumn
    width="fill"
    height="hug"
    gap="small"
    data-test="workout-set-logger"
  >
    <iRow
      v-for="row in rows"
      :key="row.setNumber"
      width="fill"
      height="hug"
      gap="small"
      vertical-align="middle"
      wrap="nowrap"
      :data-test="`workout-set-${row.setNumber}`"
    >
      <iText
        width="hug"
        min-width="fit-content"
        :wrap="false"
        :overflow="true"
      >
        Set {{ row.setNumber }}
      </iText>
      <template v-if="isStretch">
        <iTextInput
          :model-value="fieldValue(row.setNumber, 'durationSeconds')"
          type="number"
          :placeholder="durationPlaceholder"
          width="fill"
          :min-width="0"
          :disabled="row.isLogged"
          data-test="workout-set-duration"
          @update:model-value="value => setField(row.setNumber, 'durationSeconds', value)"
        />
      </template>
      <template v-else>
        <iTextInput
          :model-value="fieldValue(row.setNumber, 'reps')"
          type="number"
          :placeholder="REPS_PLACEHOLDER"
          width="fill"
          :min-width="0"
          :disabled="row.isLogged"
          data-test="workout-set-reps"
          @update:model-value="value => setField(row.setNumber, 'reps', value)"
        />
        <iTextInput
          :model-value="fieldValue(row.setNumber, 'weight')"
          type="number"
          :placeholder="weightPlaceholder"
          width="fill"
          :min-width="0"
          :disabled="row.isLogged"
          data-test="workout-set-weight"
          @update:model-value="value => setField(row.setNumber, 'weight', value)"
        />
      </template>
      <iButton
        size="small"
        :variant="row.isLogged ? 'primary' : 'secondary'"
        :is-loading="row.isSaving"
        :disabled="row.isSaving || isShowingExample"
        :aria-pressed="row.isLogged"
        data-test="workout-set-toggle"
        @click="toggleSet(row)"
      >
        {{ row.isLogged ? "Done" : "Log" }}
      </iButton>
    </iRow>
  </iColumn>
</template>

<script>
import { StoryPageElementMixin } from "%/dynamic-component-editor/standard-pages/story-page/story-page-element-mixin";
import LayoutWidthHeightOptions from "%/dynamic-component-editor/bundles/layout-width-height-options";
import { useContinueActivityStore } from "%/dynamic-component-editor/stores/continue-activity-store";
import { readMetadataValue } from "%/dynamic-components/render-engine/constants/story-type-metadata-fields";

const STRETCH_STORY_TYPE = "stretch";
const MINIMUM_SETS = 1;
const EXAMPLE_SETS = 3;
const REPS_PLACEHOLDER = "Reps";

function leadingNumber(value) {
  const number = parseFloat(value);
  return Number.isFinite(number) ? number : 0;
}

function withUnit(label, unit) {
  return unit ? `${label} (${unit})` : label;
}

export default {
  name: "WorkoutSetLogger",
  elementPanelSettings: {
    displayName: "Workout Set Logger",
    displayIcon: "mdi:dumbbell",
    elementGroup: "storyElements",
    elementType: "element",
  },
  mixins: [StoryPageElementMixin],
  optionBundles: [
    new LayoutWidthHeightOptions({
      "default.layout-width": "fill",
      "default.layout-height": "hug",
    }),
  ],
  data() {
    return {
      drafts: {},
      REPS_PLACEHOLDER,
    };
  },
  computed: {
    isShowingExample() {
      return this.editMode || this.isPreviewMode;
    },
    isStretch() {
      return this.entity?.storyType === STRETCH_STORY_TYPE;
    },
    store() {
      return useContinueActivityStore();
    },
    prescribed() {
      const metadata = this.entity?.metadata;
      return {
        sets: Math.max(Math.floor(leadingNumber(readMetadataValue(metadata, "sets"))), MINIMUM_SETS),
        reps: leadingNumber(readMetadataValue(metadata, "reps")),
        weight: leadingNumber(readMetadataValue(metadata, "weight")),
        weightUnit: readMetadataValue(metadata, "weight_descriptor") ?? "",
        duration: leadingNumber(readMetadataValue(metadata, "duration")),
        durationUnit: readMetadataValue(metadata, "duration_descriptor") ?? "",
      };
    },
    setCount() {
      if (this.isShowingExample) {
        return EXAMPLE_SETS;
      }
      return Math.max(this.store.prescribedSetsByStory[this.storyEntityId] ?? 0, this.prescribed.sets);
    },
    loggedByNumber() {
      const sets = this.store.setsByStory[this.storyEntityId] ?? [];
      return Object.fromEntries(sets.map(set => [set.setNumber, set]));
    },
    rows() {
      return Array.from({ length: this.setCount }, (_, index) => {
        const setNumber = index + 1;
        return {
          setNumber,
          isLogged: Boolean(this.loggedByNumber[setNumber]),
          isSaving: this.store.isSavingSet(this.storyEntityId, setNumber),
        };
      });
    },
    prescribedValues() {
      return {
        reps: this.prescribed.reps,
        weight: this.prescribed.weight,
        durationSeconds: this.prescribed.duration,
      };
    },
    weightPlaceholder() {
      return withUnit("Weight", this.prescribed.weightUnit);
    },
    durationPlaceholder() {
      return withUnit("Duration", this.prescribed.durationUnit);
    },
  },
  created() {
    if (this.isShowingExample || !this.storyEntityId) {
      return;
    }
    this.store.loadSetLog(this.storyEntityId);
  },
  methods: {
    fieldValue(setNumber, field) {
      const typed = this.drafts[setNumber]?.[field];
      if (typed !== undefined) {
        return typed;
      }
      const logged = this.loggedByNumber[setNumber];
      if (logged) {
        return logged[field];
      }
      return this.prescribedValues[field] || "";
    },
    setField(setNumber, field, value) {
      this.drafts[setNumber] = { ...this.drafts[setNumber], [field]: value };
    },
    toggleSet(row) {
      if (this.isShowingExample) {
        return;
      }
      if (row.isLogged) {
        this.store.removeSet(this.storyEntityId, row.setNumber);
        return;
      }
      this.store.logSet(this.storyEntityId, {
        setNumber: row.setNumber,
        reps: leadingNumber(this.fieldValue(row.setNumber, "reps")),
        weight: leadingNumber(this.fieldValue(row.setNumber, "weight")),
        durationSeconds: leadingNumber(this.fieldValue(row.setNumber, "durationSeconds")),
      }).then(() => {
        delete this.drafts[row.setNumber];
      });
    },
  },
};
</script>
